import React, { useEffect, useState } from 'react';
import { Button, Input } from 'antd';

import FilterIdsLine from './FilterIdsLine';
import { copyTextToClipboard } from '../../utils/clipboard';

/**
 * A stored definition's text, one click away (KPR-952; docs/platform-standards.md
 * §15). Closed by default: the person reads the definition in words or chips and
 * opens **Show as text** only if they need the stored text, to read an id or to
 * paste it for a developer or an agent. Open, it shows the text read-only, every
 * `#id` in it explained beneath (FilterIdsLine, when `fields` are given) and a
 * Copy button that copies the text exactly.
 *
 *   text      the stored text
 *   label     the aria-label of the text box ("Filter as text")
 *   fields    the filter vocabulary the ids are explained from (optional)
 *   explain   false when the text holds nothing FilterIdsLine reads (a formula)
 */

/** The Copy button every place that shows stored text carries. Copies `text` exactly. */
export function CopyTextButton({ text, size }) {
	const [copied, setCopied] = useState('');
	useEffect(() => {
		if (!copied) return undefined;
		const timer = setTimeout(() => setCopied(''), 2000);
		return () => clearTimeout(timer);
	}, [copied]);
	if (!String(text || '').trim()) return null;
	const handleCopy = async () => setCopied((await copyTextToClipboard(String(text))) ? 'copied' : 'failed');
	return (
		<Button size={size} onClick={handleCopy}>
			{copied === 'copied' ? 'Copied' : (copied === 'failed' ? 'Couldn’t copy' : 'Copy')}
		</Button>
	);
}

export default function ShowAsText({ text = '', label = 'Stored text', fields = [], explain = true, className = '' }) {
	const [open, setOpen] = useState(false);
	if (!String(text || '').trim()) return null;
	return (
		<div className={['show-as-text', className].filter(Boolean).join(' ')}>
			<Button type="link" size="small" className="show-as-text__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
				{open ? 'Hide text' : 'Show as text'}
			</Button>
			{open ? (
				<div className="show-as-text__body">
					<Input.TextArea readOnly aria-label={label} autoSize={{ minRows: 1, maxRows: 6 }} value={text} />
					{explain ? <FilterIdsLine text={text} fields={fields} /> : null}
					<CopyTextButton text={text} size="small" />
				</div>
			) : null}
		</div>
	);
}
